import { useState, useCallback, useEffect, useRef } from 'react'
import {
  createPaymentIntent,
  type PaymentMethodType,
} from '../services/paymentService'

type PaymentStatus = 'paid' | 'processing' | 'pending' | null | undefined

export interface PaymentBreakdown {
  basePriceCents: number
  serviceFeeCents: number
  chargedCents: number
  method: PaymentMethodType
}

interface UsePaymentIntentInput {
  eventId?: string
  inviteId?: string
  myPaymentStatus?: PaymentStatus
  eventPrice?: number | null
  authenticated: boolean
  inviteResponse?: 'yes' | 'no' | null
}

export interface UsePaymentIntentResult {
  clientSecret: string | null
  breakdown: PaymentBreakdown | null
  loading: boolean
  error: string | null
  refetch: (method?: PaymentMethodType) => Promise<void>
  cancel: () => void
}

export function usePaymentIntent({
  eventId,
  inviteId,
  myPaymentStatus,
  eventPrice,
  authenticated,
  inviteResponse,
}: UsePaymentIntentInput): UsePaymentIntentResult {
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [breakdown, setBreakdown] = useState<PaymentBreakdown | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Generation counter — every fetch bumps it; only the most recent fetch
  // is allowed to write back into state. cancel() also bumps it.
  const generationRef = useRef(0)

  const refetch = useCallback(
    async (method: PaymentMethodType = 'card') => {
      if (!eventId || !inviteId || !eventPrice || eventPrice <= 0) return
      const gen = ++generationRef.current
      setLoading(true)
      setError(null)
      try {
        const res = await createPaymentIntent(eventId, inviteId, method)
        if (gen !== generationRef.current) return
        setClientSecret(res.clientSecret)
        if (
          res.basePriceCents != null &&
          res.serviceFeeCents != null &&
          res.chargedCents != null &&
          res.method != null
        ) {
          setBreakdown({
            basePriceCents: res.basePriceCents,
            serviceFeeCents: res.serviceFeeCents,
            chargedCents: res.chargedCents,
            method: res.method,
          })
        }
        setLoading(false)
      } catch (err) {
        if (gen !== generationRef.current) return
        setError(err instanceof Error ? err.message : 'Failed to load payment')
        setLoading(false)
      }
    },
    [eventId, inviteId, eventPrice]
  )

  const cancel = useCallback(() => {
    generationRef.current++
    setClientSecret(null)
    setBreakdown(null)
    setLoading(false)
    setError(null)
  }, [])

  // Auto-fire when either:
  //   1. Server already created a pending payment row (the standard refresh path), OR
  //   2. User has RSVP'd yes on a paid event but no payment row exists yet — this
  //      can happen if the earlier (pre-fix) RSVP flow ever skipped/missed creating
  //      one. The server's gathering-payment-intent will create one fresh.
  useEffect(() => {
    const needsPayment =
      myPaymentStatus === 'pending' ||
      (inviteResponse === 'yes' && myPaymentStatus == null)

    if (
      authenticated &&
      needsPayment &&
      eventPrice &&
      eventPrice > 0 &&
      eventId &&
      inviteId
    ) {
      refetch()
    }
  }, [authenticated, myPaymentStatus, inviteResponse, eventPrice, eventId, inviteId, refetch])

  return { clientSecret, breakdown, loading, error, refetch, cancel }
}
